import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { consultaEsquema, useDonoEsquema } from '../dicionario/useEsquema';
import { consultaLista } from '../paginas/dadosLista';
import { useSessao } from '../sessao/sessao';

/** Tempo parado sobre a rotina antes de buscar: atravessar o menu com o mouse não dispara nada. */
const ESPERA_MS = 150;

/**
 * Pré-carrega a rotina quando o mouse para sobre ela (ou ela recebe foco): esquema e o primeiro bloco da lista, na mesma
 * chave de cache que a página usa. Com o servidor a ~150 ms de distância, o clique encontra a tela pronta.
 * Devolve os manipuladores para espalhar no link: `{...preCarregar('CAD010')}`.
 */
export function usePreCarregar() {
  const cliente = useQueryClient();
  const dono = useDonoEsquema();
  const { empresa, filial } = useSessao();
  const espera = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (espera.current) clearTimeout(espera.current); }, []);

  const carregar = async (codigo: string) => {
    try {
      const e = await cliente.fetchQuery(consultaEsquema(cliente, codigo, dono));
      // Só a lista gerada pelo dicionário: tela própria ou fonte do cliente buscam do seu jeito.
      if (!e.alias || e.componente || e.fonte) return;
      await cliente.prefetchInfiniteQuery(consultaLista({ empresa: empresa?.codigo, filial: filial?.codigo, alias: e.alias, q: '', ordem: e.ordemPadrao }));
    } catch { /* pré-carregar é só adiantamento: o erro aparece quando a pessoa abrir a rotina */ }
  };

  return (codigo: string) => {
    const agendar = () => {
      if (espera.current) clearTimeout(espera.current);
      espera.current = setTimeout(() => void carregar(codigo), ESPERA_MS);
    };
    const cancelar = () => {
      if (espera.current) clearTimeout(espera.current);
      espera.current = null;
    };
    // Foco pelo teclado usa a mesma espera: descer o menu com as setas não pede cada rotina do caminho.
    return { onMouseEnter: agendar, onMouseLeave: cancelar, onFocus: agendar, onBlur: cancelar };
  };
}
